import * as React from "react";
import { Modal, notification } from "antd";
import { request, resourceLink } from "../services/cpln";
import { Secret } from "../schema/types/secret/entity";
import { EnvVarInput } from "../schema/types/workload/input";

export interface RevealState {
  isRevealing: boolean;
  isRevealed: boolean;
  revealedValues: Record<string, string>;
  errors: Record<string, string>;
}

const initialState: RevealState = {
  isRevealing: false,
  isRevealed: false,
  revealedValues: {},
  errors: {},
};

function extractSecretValue(secret: Secret, suffix: string): string {
  const { type, data } = secret;

  if (suffix) {
    if (type === "dictionary") {
      const dict = data as Record<string, string>;
      return dict[suffix] ?? `[property "${suffix}" not found]`;
    }
    if (typeof data === "object" && data !== null) {
      const val = (data as any)[suffix];
      if (val === undefined) return `[property "${suffix}" not found]`;
      return String(val);
    }
    return `[cannot resolve property "${suffix}"]`;
  }

  if (type === "opaque") {
    if (typeof data === "string") return data;
    const opaque = data as { payload?: string };
    return opaque.payload ?? JSON.stringify(data);
  }
  if (type === "dictionary") {
    return JSON.stringify(data, null, 2);
  }
  if (typeof data === "string") return data;
  return JSON.stringify(data, null, 2);
}

export function useRevealEnvSecrets(envVars: EnvVarInput[]) {
  const [revealState, setRevealState] = React.useState<RevealState>(initialState);

  const handleRevealAll = React.useCallback(() => {
    const secretEnvVars = envVars.filter((e) => e.prefix === "cpln://secret/" && e.cplnSecret.value);
    if (secretEnvVars.length === 0) return;

    Modal.confirm({
      title: "Reveal Secret Values",
      content: "Revealing secrets will generate an audit trail log for each secret. Do you want to continue?",
      okText: "Reveal",
      onOk: async () => {
        setRevealState((prev) => ({ ...prev, isRevealing: true }));

        const uniqueSecretNames = Array.from(new Set(secretEnvVars.map((e) => e.cplnSecret.value)));
        const revealedSecrets: Record<string, Secret> = {};
        const secretErrors: Record<string, string> = {};

        await Promise.all(
          uniqueSecretNames.map(async (secretName) => {
            try {
              const secretLink = resourceLink("secret", secretName);
              const { data } = await request<Secret>({ url: `${secretLink}/-reveal` });
              revealedSecrets[secretName] = data;
            } catch (e: any) {
              const status = e?.response?.status;
              if (status === 403) {
                secretErrors[secretName] = `Permission denied: cannot reveal secret "${secretName}"`;
              } else if (status === 404) {
                secretErrors[secretName] = `Secret "${secretName}" not found`;
              } else {
                secretErrors[secretName] = `Failed to reveal secret "${secretName}"`;
              }
            }
          }),
        );

        const revealedValues: Record<string, string> = {};
        const errors: Record<string, string> = {};

        for (const ev of secretEnvVars) {
          const secretName = ev.cplnSecret.value;
          if (secretErrors[secretName]) {
            errors[ev.id] = secretErrors[secretName];
            continue;
          }
          const secret = revealedSecrets[secretName];
          if (!secret) {
            errors[ev.id] = `Secret "${secretName}" could not be resolved`;
            continue;
          }
          try {
            revealedValues[ev.id] = extractSecretValue(secret, ev.suffix);
          } catch {
            errors[ev.id] = `Failed to extract value from secret "${secretName}"`;
          }
        }

        const failedNames = Object.keys(secretErrors);
        const totalSecrets = uniqueSecretNames.length;

        if (failedNames.length === 0) {
          notification.success({ message: "Secrets Revealed", description: "All secret values resolved successfully" });
        } else if (failedNames.length === totalSecrets) {
          notification.error({
            message: "Failed to Reveal Secrets",
            description: `Could not reveal: ${failedNames.join(", ")}`,
          });
        } else {
          notification.warning({
            message: "Some Secrets Could Not Be Revealed",
            description: `Failed: ${failedNames.join(", ")}`,
          });
        }

        setRevealState({ isRevealing: false, isRevealed: true, revealedValues, errors });
      },
    });
  }, [envVars]);

  const handleHideAll = React.useCallback(() => {
    setRevealState(initialState);
  }, []);

  return { revealState, handleRevealAll, handleHideAll };
}
